import React, { useEffect, useState } from "react";
import { Brain, Calculator, ChevronDown, ClipboardCheck, FileText, Waves, Package } from "lucide-react";
import { toast } from "sonner";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { listPricingStrategies } from "../../lib/api";

/**
 * AI tools toolbar — Analyze + Estimate split-buttons with saved-results
 * dropdowns. Extracted from WorkspacePage.jsx in iter-20.
 *
 * Every data-testid and class name matches the original verbatim so
 * existing UI tests are unaffected.
 */
export default function AIToolbar({
    files = [],
    analyses = [],
    estimates = [],
    onOpenAnalyze,
    onOpenEstimate,
    onOpenAsBuilt,
    hasAsBuilt = false,
    asBuiltLoading = false,
    onOpenSchedule,
    scheduleLoading = 0,
    onOpenSeptic,
    onPrintPack,
    hasSchedules = false,
    onPickAnalysis,
    onPickEstimate,
}) {
    const hasFiles = files.length > 0;

    // iter-71: strategy labels from the admin-customisable endpoint.
    const [stratLabels, setStratLabels] = useState({
        competitive: "Cost",
        average: "Competitive",
        high: "Average",
    });
    useEffect(() => {
        (async () => {
            try {
                const d = await listPricingStrategies();
                const map = {};
                for (const s of d?.strategies || []) {
                    if (s.id) map[s.id] = s.label || s.id;
                }
                if (Object.keys(map).length) setStratLabels((p) => ({ ...p, ...map }));
            } catch { /* fallback */ }
        })();
    }, []);

    const guardOpen = (fn, label) => () => {
        if (!hasFiles && label === "Analyze") {
            toast.message("Upload a PDF first", {
                description: `${label} needs at least one plan to read.`,
            });
            return;
        }
        fn();
    };

    return (
        <div
            className="border-b border-white/15 bg-[#111] backdrop-blur-xl"
            data-testid="ai-toolbar"
        >
            <div className="max-w-[1600px] mx-auto flex flex-nowrap items-center gap-1.5 px-2 md:px-6 py-1.5 overflow-x-auto md:overflow-x-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {/* iter-173: removed the inactive Sparkles "AI Tools"
                    label — it wasn't a button and was eating space. All
                    remaining tools now sit on a single non-wrapping row
                    with tighter gap/padding so they fit tablet-portrait
                    without a second row. */}
                {/* Analyze split-button */}
                <div
                    className="shrink-0 inline-flex items-stretch rounded-sm border border-white/15 bg-[#111] overflow-hidden group/analyze hover:border-[#1a3fd1]/60 transition-colors data-[disabled=true]:opacity-40 data-[disabled=true]:hover:border-white/25"
                    data-disabled={!hasFiles}
                >
                    <button
                        type="button"
                        onClick={guardOpen(onOpenAnalyze, "Analyze")}
                        disabled={!hasFiles}
                        data-testid="ai-tool-analyze"
                        className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-white text-xs font-semibold group-hover/analyze:text-[#8fa4f0] transition-colors disabled:cursor-not-allowed disabled:group-hover/analyze:text-white"
                        title="Analyze"
                        aria-label="Analyze"
                    >
                        <Brain className="w-3.5 h-3.5 text-[#8fa4f0]" />
                        <span className="hidden sm:inline">Analyze</span>
                    </button>

                    {analyses.length > 0 && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="ai-saved-analyses-trigger"
                                    className="flex items-center gap-1 px-2 py-1.5 border-l border-white/15 text-slate-200 text-xs font-semibold hover:bg-white/10 hover:text-white transition-colors"
                                    aria-label="Open saved analyses"
                                >
                                    <span className="min-w-[16px] px-1 rounded-sm bg-[#1a3fd1]/20 border border-[#1a3fd1]/40 text-[10px] font-bold tabular-nums text-[#8fa4f0] leading-[14px] text-center">
                                        {analyses.length}
                                    </span>
                                    <ChevronDown className="w-3 h-3 text-slate-400" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                data-testid="ai-saved-analyses-menu"
                                className="bg-[#111] border border-white/15 text-white min-w-[260px] max-h-[60vh] overflow-y-auto"
                            >
                                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">
                                    Saved analyses
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-[#1a1a1a]" />
                                {analyses.map((a) => {
                                    const scopeText =
                                        a.scope === "trade" && a.trade
                                            ? a.trade.replace(/_/g, " ")
                                            : "Overall";
                                    return (
                                        <DropdownMenuItem
                                            key={a.id}
                                            onClick={() => onPickAnalysis && onPickAnalysis(a.id)}
                                            data-testid={`ai-analysis-item-${a.id}`}
                                            className="flex items-start gap-2 py-2 px-2 focus:bg-[#1a1a1a] focus:text-white cursor-pointer"
                                        >
                                            <Brain className="w-3.5 h-3.5 text-[#8fa4f0] mt-0.5 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-white truncate">
                                                    {a.label}
                                                </div>
                                                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5 flex items-center gap-1.5">
                                                    <span className="text-[#8fa4f0]">{scopeText}</span>
                                                    <span>·</span>
                                                    <span>{a.style}</span>
                                                </div>
                                            </div>
                                        </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>

                {/* Estimate split-button */}
                <div
                    className="shrink-0 inline-flex items-stretch rounded-sm border border-white/15 bg-[#111] overflow-hidden group/estimate hover:border-[#1a3fd1]/60 transition-colors"
                >
                    <button
                        type="button"
                        onClick={guardOpen(onOpenEstimate, "Estimate")}
                        data-testid="ai-tool-estimate"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold group-hover/estimate:text-[#8fa4f0] transition-colors"
                        title="Estimate"
                        aria-label="Estimate"
                    >
                        <Calculator className="w-3.5 h-3.5 text-[#8fa4f0]" />
                        <span className="hidden sm:inline">Estimate</span>
                    </button>

                    {estimates.length > 0 && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="ai-saved-estimates-trigger"
                                    className="flex items-center gap-1 px-2 py-1.5 border-l border-white/15 text-slate-200 text-xs font-semibold hover:bg-white/10 hover:text-white transition-colors"
                                    aria-label="Open saved estimates"
                                >
                                    <span className="min-w-[16px] px-1 rounded-sm bg-[#1a3fd1]/20 border border-[#1a3fd1]/40 text-[10px] font-bold tabular-nums text-[#8fa4f0] leading-[14px] text-center">
                                        {estimates.length}
                                    </span>
                                    <ChevronDown className="w-3 h-3 text-slate-400" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                data-testid="ai-saved-estimates-menu"
                                className="bg-[#111] border border-white/15 text-white min-w-[260px] max-h-[60vh] overflow-y-auto"
                            >
                                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">
                                    Saved estimates
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator className="bg-[#1a1a1a]" />
                                {estimates.map((e) => {
                                    const strat = (e.pricing_strategy || "average").toLowerCase();
                                    const stratMeta = strat === "competitive"
                                        ? { label: stratLabels.competitive || "Cost", cls: "text-[#00C853] bg-[#00C853]/10 border border-[#00C853]/30" }
                                        : strat === "high"
                                        ? { label: stratLabels.high || "Average", cls: "text-[#FFA726] bg-[#FFA726]/10 border border-[#FFA726]/30" }
                                        : { label: stratLabels.average || "Competitive", cls: "text-[#8fa4f0] bg-[#1a3fd1]/10 border border-[#1a3fd1]/30" };
                                    return (
                                    <DropdownMenuItem
                                        key={e.id}
                                        onClick={() => onPickEstimate && onPickEstimate(e.id)}
                                        data-testid={`ai-estimate-item-${e.id}`}
                                        className="flex items-start gap-2 py-2 px-2 focus:bg-[#1a1a1a] focus:text-white cursor-pointer"
                                    >
                                        <Calculator className="w-3.5 h-3.5 text-[#8fa4f0] mt-0.5 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-sm text-white truncate">
                                                    {e.label}
                                                </span>
                                                <span
                                                    data-testid={`ai-estimate-strategy-${e.id}`}
                                                    className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider shrink-0 ${stratMeta.cls}`}
                                                >
                                                    {stratMeta.label}
                                                </span>
                                            </div>
                                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5 flex items-center gap-1.5">
                                                <span className="text-[#8fa4f0]">
                                                    ${Number(e.totals?.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                                <span>·</span>
                                                <span>{e.lines?.length || 0} lines</span>
                                            </div>
                                        </div>
                                    </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>

                {/* iter-168 — As-Built button next to Estimate. Loads
                    (or generates once) an as-built page from the shipping
                    template and adds it to the project's Files sidebar. */}
                <button
                    type="button"
                    onClick={() => onOpenAsBuilt && onOpenAsBuilt()}
                    disabled={asBuiltLoading || !onOpenAsBuilt}
                    data-testid="ai-tool-asbuilt"
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-white/15 bg-[#111] text-white text-xs font-semibold hover:border-[#1a3fd1]/60 hover:text-[#8fa4f0] transition-colors disabled:opacity-50 disabled:cursor-wait"
                    title={hasAsBuilt ? "Open As-Built" : "Create As-Built"}
                    aria-label={hasAsBuilt ? "Open As-Built" : "Create As-Built"}
                >
                    <ClipboardCheck className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="hidden sm:inline">
                        {asBuiltLoading ? "Working…" : (hasAsBuilt ? "As-Built" : "As-Built")}
                    </span>
                </button>

                {/* iter-169p — Schedule 1 / 2 / 3 quick-open buttons.
                    Icon + bare digit at every breakpoint so the bar
                    fits mobile without horizontal scroll. Tooltip on
                    hover shows the full "Schedule N" name. */}
                {[1, 2, 3].map((n) => (
                    <button
                        key={n}
                        type="button"
                        onClick={() => onOpenSchedule && onOpenSchedule(n)}
                        disabled={scheduleLoading === n || !onOpenSchedule}
                        data-testid={`ai-tool-schedule-${n}`}
                        className="shrink-0 inline-flex items-center gap-1 px-2 py-1.5 rounded-sm border border-white/15 bg-[#111] text-white text-xs font-semibold hover:border-[#1a3fd1]/60 hover:text-[#8fa4f0] transition-colors disabled:opacity-50 disabled:cursor-wait"
                        title={`Schedule ${n}`}
                        aria-label={`Open Schedule ${n}`}
                    >
                        <FileText className="w-3.5 h-3.5 text-amber-400" />
                        <span>{scheduleLoading === n ? "…" : n}</span>
                    </button>
                ))}

                {/* iter-169k — Septic design side-sheet trigger. Only
                    renders when a parent supplies the handler. */}
                {onOpenSeptic && (
                    <button
                        type="button"
                        onClick={onOpenSeptic}
                        data-testid="ai-tool-septic-design"
                        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-white/15 bg-[#111] text-white text-xs font-semibold hover:border-[#1a3fd1]/60 hover:text-[#8fa4f0] transition-colors"
                        title="Septic design specs (feeds Schedule 3)"
                        aria-label="Septic design"
                    >
                        <Waves className="w-3.5 h-3.5 text-cyan-400" />
                        <span className="hidden sm:inline">Septic</span>
                    </button>
                )}

                {/* iter-169m — Batch print pack: download S1+S2+S3
                    flattened in a ZIP for permit-office submission. */}
                {onPrintPack && hasSchedules && (
                    <button
                        type="button"
                        onClick={onPrintPack}
                        data-testid="ai-tool-print-pack"
                        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-white/15 bg-[#111] text-white text-xs font-semibold hover:border-[#1a3fd1]/60 hover:text-[#8fa4f0] transition-colors"
                        title="Download every schedule flattened in one ZIP"
                        aria-label="Print pack"
                    >
                        <Package className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="hidden sm:inline">Print pack</span>
                    </button>
                )}

                {/* iter-170: plugin toggles moved into the Estimate /
                    Analyze dialogs — this toolbar no longer carries a
                    plugin collapse button. */}
            </div>
        </div>
    );
}
